import React, { useCallback, useEffect, useRef, useState } from 'react';
import AccountDialog from './AccountDialog';
import styles from './Account.module.css';
import { ImageError, videoFrameToAvatarDataUrl } from '../../utils/imageResize';

/**
 * In-app camera, for devices with a webcam but no native camera app worth
 * handing off to — desktops, in practice. Phones and tablets get the OS camera
 * through a `capture` file input instead, which is faster and better.
 *
 * The preview is mirrored because an un-mirrored view of your own face is
 * disconcerting; the capture is mirrored to match, so the photo looks like the
 * preview it was taken from.
 */
export default function CameraDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | live | denied | error
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setStatus('starting');
    setError('');

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Autoplay needs the explicit call in Safari.
          videoRef.current.play?.().catch(() => {});
        }
        setStatus('live');
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus(e?.name === 'NotAllowedError' ? 'denied' : 'error');
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [open, stop]);

  const handleCapture = useCallback(async () => {
    setError('');
    setIsSaving(true);
    try {
      const dataUrl = videoFrameToAvatarDataUrl(videoRef.current, { mirror: true });
      stop();
      await onCapture(dataUrl);
    } catch (e) {
      setError(
        e instanceof ImageError
          ? e.message
          : e?.response?.data?.error || 'Could not save that photo. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [onCapture, stop]);

  return (
    <AccountDialog
      open={open}
      onClose={onClose}
      title="Take a photo"
      lead={status === 'live' ? 'Centre yourself in the frame.' : undefined}
      footer={
        <>
          <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleCapture}
            disabled={status !== 'live' || isSaving}
          >
            {isSaving ? 'Saving…' : 'Take photo'}
          </button>
        </>
      }
    >
      <div className={styles.cameraStage}>
        {/* Kept mounted while starting so the stream has somewhere to attach. */}
        <video
          ref={videoRef}
          className={styles.cameraVideo}
          playsInline
          muted
          autoPlay
          data-ready={status === 'live' ? 'true' : 'false'}
        />
        {status === 'starting' ? <p className={styles.cameraNote}>Starting the camera…</p> : null}
        {status === 'denied' ? (
          <p className={styles.cameraNote}>
            Camera access was blocked. Allow it in your browser’s site settings, or upload a photo
            from your files instead.
          </p>
        ) : null}
        {status === 'error' ? (
          <p className={styles.cameraNote}>
            No camera was available. Upload a photo from your files instead.
          </p>
        ) : null}
      </div>

      {error ? (
        <p className={styles.formError} role="alert">
          {error}
        </p>
      ) : null}
    </AccountDialog>
  );
}
